import { createContext, useContext, useState, useRef, useEffect, ReactNode } from "react";
import { DailyRecap, dailyRecapService } from "@/services/dailyRecapService";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { confetti } from "@/lib/confetti";
import { useRecapAudioMap } from "@/hooks/useRecapAudio";

interface DailyRecapPlayerContextType {
  currentRecap: DailyRecap | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: number;
  autoPlayEnabled: boolean;
  recaps: DailyRecap[];
  currentIndex: number;
  hasNext: boolean;
  hasPrevious: boolean;
  playRecap: (recap: DailyRecap) => void;
  pause: () => void;
  resume: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  stopPlayback: () => void;
  playNext: () => void;
  playPrevious: () => void;
  toggleAutoPlay: () => void;
}

const DailyRecapPlayerContext = createContext<DailyRecapPlayerContextType | undefined>(undefined);

export const useDailyRecapPlayer = () => {
  const context = useContext(DailyRecapPlayerContext);
  if (!context) {
    throw new Error("useDailyRecapPlayer must be used within DailyRecapPlayerProvider");
  }
  return context;
};

export const DailyRecapPlayerProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [currentRecap, setCurrentRecap] = useState<DailyRecap | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [recaps, setRecaps] = useState<DailyRecap[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const { resolve: resolveRecapAudio } = useRecapAudioMap();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasMarkedStarted = useRef(false);
  const hasMarkedCompleted = useRef(false);

  // Load all published recaps
  useEffect(() => {
    dailyRecapService.getAllPublishedRecaps().then(setRecaps).catch(console.error);
  }, []);

  const hasNext = currentIndex < recaps.length - 1;
  const hasPrevious = currentIndex > 0;

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;
    }

    {
      const audio = audioRef.current;

      const handleTimeUpdate = () => {
        const time = audio.currentTime;
        setCurrentTime(time);

        // Mark as started
        if (time > 1 && !hasMarkedStarted.current && currentRecap && user) {
          hasMarkedStarted.current = true;
          dailyRecapService.markRecapStarted(currentRecap.id).catch(console.error);
        }

        // Mark as completed at 95%
        if (duration > 0 && time / duration >= 0.95 && !hasMarkedCompleted.current && currentRecap && user) {
          hasMarkedCompleted.current = true;
          dailyRecapService.markRecapCompleted(currentRecap.id)
            .then((result) => {
              confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
              });
              toast.success(`🎉 Daily Recap Complete! +${result.xp_earned} XP`);
            })
            .catch(console.error);
        }
      };

      const handleLoadedMetadata = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      handleLoadedMetadata();

      const handleEnded = () => {
        setIsPlaying(false);
        
        // Auto-play next if enabled
        if (autoPlayEnabled && hasNext) {
          setTimeout(() => playNext(), 1000);
        }
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('durationchange', handleLoadedMetadata);
      audio.addEventListener('canplay', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('durationchange', handleLoadedMetadata);
        audio.removeEventListener('canplay', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [currentRecap, user, duration, autoPlayEnabled, hasNext]);

  // Sync volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Sync playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const playRecap = (recap: DailyRecap) => {
    if (!audioRef.current) return;

    // If same recap, just resume
    if (currentRecap?.id === recap.id) {
      resume();
      return;
    }

    // Load new recap
    setCurrentRecap(recap);
    hasMarkedStarted.current = false;
    hasMarkedCompleted.current = false;
    
    audioRef.current.src = resolveRecapAudio(recap.id, recap.audio_url) ?? '';
    audioRef.current.load();
    
    // Play when ready
    audioRef.current.play().then(() => {
      setIsPlaying(true);
    }).catch(console.error);

    // Update current index
    const index = recaps.findIndex(r => r.id === recap.id);
    if (index >= 0) {
      setCurrentIndex(index);
    }
  };

  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };

  const resume = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => {
      setIsPlaying(true);
    }).catch(console.error);
  };

  const seek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const setVolume = (newVolume: number) => {
    setVolumeState(newVolume);
  };

  const setPlaybackRate = (rate: number) => {
    setPlaybackRateState(rate);
  };

  const stopPlayback = () => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentRecap(null);
    setCurrentTime(0);
    hasMarkedStarted.current = false;
    hasMarkedCompleted.current = false;
  };

  const playNext = () => {
    if (hasNext && recaps[currentIndex + 1]) {
      playRecap(recaps[currentIndex + 1]);
    }
  };

  const playPrevious = () => {
    if (hasPrevious && recaps[currentIndex - 1]) {
      playRecap(recaps[currentIndex - 1]);
    }
  };

  const toggleAutoPlay = () => {
    setAutoPlayEnabled(prev => !prev);
  };

  return (
    <DailyRecapPlayerContext.Provider
      value={{
        currentRecap,
        isPlaying,
        currentTime,
        duration,
        volume,
        playbackRate,
        autoPlayEnabled,
        recaps,
        currentIndex,
        hasNext,
        hasPrevious,
        playRecap,
        pause,
        resume,
        seek,
        setVolume,
        setPlaybackRate,
        stopPlayback,
        playNext,
        playPrevious,
        toggleAutoPlay,
      }}
    >
      {children}
    </DailyRecapPlayerContext.Provider>
  );
};
